import React, { useState, useEffect } from 'react';

import Create from './Create';
import Details from './Details';
import { ActionIcon, Group, LoadingOverlay, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useTranslations } from 'shared/translations/useTranslations';
import useFetch from 'shared/hooks/useFetch';
import apiService from 'shared/services/api';
import { Trash, Pencil } from 'tabler-icons-react';
import AppContent from 'shared/components/AppContent';
import { VisualInspectionQuestionModel } from './VisualInspection';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';
import styles from './styles.module.scss';
import ListHeading from 'shared/components/ListHeading';
import DeleteQuestionModal from './DeleteQuestionModal';

const DragHandle = SortableHandle(({ children }) => (
  <span className={styles.orderHandle}>{children}</span>
));

const SortableItem = SortableElement(
  ({
    value: question,
    order,
    setSelectedEditId,
    setSelectedDeleteId,
  }: {
    value: VisualInspectionQuestionModel;
    order: number;
    setSelectedEditId: any;
    setSelectedDeleteId: any;
  }) => {
    return (
      <div>
        <Group>
          <DragHandle>{order + 1}.</DragHandle>
          <div style={{ width: '90%' }}>
            <Group>
              {question.question}
              <Group ml="auto">
                <ActionIcon
                  variant="transparent"
                  onClick={() => {
                    setSelectedEditId(question.id);
                  }}>
                  <Pencil size={16} />
                </ActionIcon>
                <ActionIcon
                  variant="transparent"
                  onClick={() => {
                    setSelectedDeleteId(question.id);
                  }}>
                  <Trash size={16} />
                </ActionIcon>
                {/* <ActionIcon variant="transparent">
                  <Trash size={16} />
                </ActionIcon> */}
              </Group>
            </Group>
            <div>
              <Text size="sm">{question.comment}</Text>
            </div>
          </div>
        </Group>
      </div>
    );
  },
);

const SortableList = SortableContainer(({ items, setSelectedEditId, setSelectedDeleteId }) => {
  return (
    <ul>
      {items.map((value: VisualInspectionQuestionModel, index) => (
        <SortableItem
          key={`item-${value.id}`}
          index={index}
          value={value}
          order={index}
          setSelectedEditId={setSelectedEditId}
          setSelectedDeleteId={setSelectedDeleteId}
        />
      ))}
    </ul>
  );
});

function VisualInspection() {
  const translations = useTranslations();
  const { data, isLoading, isLoaded, error, refresh } = useFetch<{
    questions: VisualInspectionQuestionModel[];
  }>({
    initialValue: { questions: [] },
    fetchAction: () => apiService({}).get(`/api/visual-inspection/questions`),
  });
  const [selectedEditId, setSelectedEditId] = useState<string | undefined>();
  const [selectedDeleteId, setSelectedDeleteId] = useState<string | undefined>();
  const handleRefresh = () => {
    refresh();
  };
  const handleModalClose = () => {
    setSelectedEditId(undefined);
    setSelectedDeleteId(undefined)
  };
  const [items, setItems] = useState([]);
  useEffect(() => {
    setItems(data.questions);
  }, [data.questions]);
  const [isSavingOrder, setSavingOrder] = useState(false);
  const saveQuestionsOrder = async questions => {
    const result = questions.map((question, index) => ({
      id: question.id,
      order: index,
    }));
    setSavingOrder(true);
    await apiService({}).post(`/api/visual-inspection/questions/set-order`, result);
    setSavingOrder(false);
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setItems(items => {
      const newOrder = arrayMoveImmutable(items, oldIndex, newIndex);
      saveQuestionsOrder(newOrder);
      return newOrder;
    });
  };
  return (
    <div style={{ position: 'relative' }}>
      <ListHeading title={translations.global.visualInspection}>
        <Create refreshParent={handleRefresh} onClose={handleModalClose} />
      </ListHeading>
      <AppContent>
        <LoadingOverlay visible={isLoading || isSavingOrder} />
        <SortableList
          items={items}
          onSortEnd={onSortEnd}
          useDragHandle
          setSelectedEditId={setSelectedEditId}
          setSelectedDeleteId={setSelectedDeleteId}
        />
        <Details id={selectedEditId} refreshParent={handleRefresh} onClose={handleModalClose} />
        <DeleteQuestionModal id={selectedDeleteId} refreshParent={handleRefresh} onClose={handleModalClose} />
      </AppContent>
    </div>
  );
}

export default VisualInspection;
